import "./ZoomImageBox.scss";
import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext.js";
import { storage } from "../../context/FirebaseConfig.js";
import { fetchImageUrl } from "../../shared_components/utils/ImgUtils.js";
import { hexToRgba } from "../../utils/hexToRgba.js";
import LoadingIcon from "../LoadingIcon/LoadingIcon.jsx";
import { Grid3x3, Tags } from "lucide-react";

export default function ZoomImageBox({ image_url, highlights }) {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);

  const [imgSrc, setImgSrc] = useState(null);
  const [zoomIn, setZoomIn] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [showTags, setShowTags] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageRatio, setImageRatio] = useState({ width: 0, height: 0 });

  const imgRef = useRef(null);
  const divRef = useRef(null);

  const aspectRatio = useMemo(() => {
    const width = imgRef.current?.naturalWidth || 1;
    const height = imgRef.current?.naturalHeight || 1;
    return `${width} / ${height}`;
  }, [imageLoaded]);

  const setImageDimensions = () => {
    if (imgRef.current) {
      const { naturalWidth, naturalHeight } = imgRef.current;
      setImageRatio({
        width: 100 / naturalWidth,
        height: 100 / naturalHeight,
      });
    }
  };

  const handleImageLoad = () => {
    setImageDimensions();
    setImageLoaded(true);
  };

  const handleMouseMove = (e) => {
    if (!divRef.current) return;
    const { left, top, width, height } = divRef.current.getBoundingClientRect();
    const x = ((e.pageX - left) / width) * 100;
    const y = ((e.pageY - window.scrollY - top) / height) * 100;
    divRef.current.style.transformOrigin = `${x}% ${y}%`;
  };

  useEffect(() => {
    if (!image_url) return;

    setImgSrc(null);
    setImageLoaded(false);

    fetchImageUrl(storage, image_url).then((url) => {
      setImgSrc(url)
    });
  }, [image_url]);

  useEffect(() => {
    if (imgRef.current?.naturalWidth && imgRef.current?.naturalHeight) {
      setImageDimensions();
    }
  }, [imgSrc]);

  const userHasGridAccess =
    currentUser?.info?.user_flags?.some((flag) =>
      ["labeler", "admin"].includes(flag)
    );

  const userHasTagAccess =
    currentUser?.info?.user_flags?.some((flag) =>
      ["admin"].includes(flag)
    );

  return (
    <div className="zoombox-img-container" onMouseMove={handleMouseMove}>
      <div className={`zoombox-img-holder ${zoomIn ? "zoomed" : ""}`}>
        <div
          ref={divRef}
          className={`zoombox-img-wrapper ${zoomIn ? "zoomed" : ""}`}
          style={{ aspectRatio }}
        >
          <img
            ref={imgRef}
            className={`zoombox-img ${imageLoaded ? "loaded" : ""}`}
            src={imgSrc || ""}
            alt=""
            style={{ display: imgSrc ? "block" : "none" }}
            onLoad={handleImageLoad}
            onClick={() => setZoomIn((prev) => !prev)}
          />

          {showGrid && imageLoaded && (
            <div className="grid-overlay">
              {["33.33%", "66.66%"].map((pos, i) => (
                <React.Fragment key={i}>
                  <div className="grid-line horizontal" style={{ top: pos }} />
                  <div className="grid-line vertical" style={{ left: pos }} />
                </React.Fragment>
              ))}
            </div>
          )}

            {showTags && userHasTagAccess && imageLoaded &&
            highlights.map((highlight, index) => {
              const [x1, y1, x2, y2] = highlight.dimension;
              const left = (x1 + highlight.imagePadding) * imageRatio.width;
              const top = (y1 + highlight.imagePadding) * imageRatio.height;
              const width = (x2 - x1) * imageRatio.width;
              const height = (y2 - y1) * imageRatio.height;
              const meta = highlight.meta;
              const color = meta?.display_color ?? "red";

              return (
                <div
                  key={index}
                  className="zoombox-img-highlight"
                  style={{
                    left: `${left}%`,
                    top: `${top}%`,
                    width: `${width}%`,
                    height: `${height}%`,
                    backgroundColor: hexToRgba(color, "0.2"),
                    borderColor: color,
                  }}
                >
                  {highlight.meta?.display_name && (
                    <span style={{ backgroundColor: color }}>
                      {highlight.meta.display_name}
                    </span>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {!imageLoaded && (
        <div className="loading-icon-container">
          <LoadingIcon />
        </div>
      )}

      <div className="zoombox-tool">
        {userHasTagAccess && (
        <button
          className={`grid-toggle-button ${showTags ? "active" : ""}`}
          onClick={() => setShowTags((prev) => !prev)}
          title={showTags ? t("tag_hide") : t("tag_show")}
        >
          <Tags />
        </button>
        )}
        {userHasGridAccess && (
          <button
            className={`grid-toggle-button ${showGrid ? "active" : ""}`}
            onClick={() => setShowGrid((prev) => !prev)}
            title={showGrid ? t("grid_hide") : t("grid_show")}
          >
            <Grid3x3 />
          </button>
        )}
      </div>
    </div>
  );
}
